import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { PaymentMethod } from "@redotech/redo-billing-common/payout";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

const DEFAULT_NOTIFICATION_PERCENTAGE = 0.2;

function getPaymentMethodOption(
  name: string,
  number: string,
  id: string,
): TextRedoListItem<string> {
  return { id, value: id, type: "text", text: `${name} ••••-${number}` };
}

function getPaymentMethodInfo(fundingSource: PaymentMethod) {
  if ("legacyCard" in fundingSource) {
    return getPaymentMethodOption(
      fundingSource.legacyCard.brand,
      fundingSource.legacyCard.last4,
      fundingSource.legacyCard.id,
    );
  }
  if (fundingSource.type === "us_bank_account") {
    return getPaymentMethodOption(
      fundingSource.us_bank_account.bank_name ?? "",
      fundingSource.us_bank_account.last4 ?? "",
      fundingSource.id,
    );
  }
  if (fundingSource.type === "card") {
    return getPaymentMethodOption(
      fundingSource.card.brand,
      fundingSource.card.last4,
      fundingSource.id,
    );
  }
  return undefined;
}

export const AddFundsModal = memo(function AddFundsModal({
  isOpen,
  onModalCloseRequested,
  onSuccess,
  labelPrice,
  balance,
  balanceLoading,
}: {
  isOpen: boolean;
  onModalCloseRequested: () => void;
  onSuccess: () => void;
  labelPrice: number;
  balance: string | undefined;
  balanceLoading: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const fundingSources: PaymentMethod[] = useMemo(() => {
    return [
      ...(team.settings.outboundLabels.billing?.fundingSources ?? []),
      ...(team.stripe?.paymentMethod ? [team.stripe.paymentMethod] : []),
      ...(team.stripe?.secondaryPaymentMethod
        ? [team.stripe.secondaryPaymentMethod]
        : []),
    ];
  }, [
    team.settings.outboundLabels.billing?.fundingSources,
    team.stripe?.paymentMethod,
    team.stripe?.secondaryPaymentMethod,
  ]);

  const [amount, setAmount] = useState<number>(0);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const doAddFunds = useHandler(async () => {
    if (!selectedPaymentMethodId) {
      return;
    }
    try {
      await rpcClient.addOutboundFunds({
        amount,
        currency: Currency.USD,
        paymentMethodId: selectedPaymentMethodId,
        alertThreshold: amount * DEFAULT_NOTIFICATION_PERCENTAGE,
      });
      onSuccess();
    } catch (e) {
      console.error(e);
      setErrorMessage("Failed to add funds. Please try again later.");
    }
  });

  const minAmount = labelPrice - (Number(balance) ?? 0);
  const error = !balanceLoading && amount < minAmount;

  const modalProps: RedoModalProps = {
    isOpen,
    onModalCloseRequested,
    title: "Add funds to your account",
    subtitle: `Current balance: ${balanceLoading ? "loading..." : `$${balance}`}`,
    primaryButton: {
      text: "Add funds & purchase label",
      onClick: doAddFunds,
      disabled: error || !selectedPaymentMethodId,
    },
    secondaryButton: { text: "Cancel", onClick: onModalCloseRequested },
    modalSize: "sm",
  };

  return (
    <RedoModal {...modalProps}>
      <Flex dir="column" gap="lg">
        <RedoSingleSelectDropdownInput
          label="Funding source"
          options={fundingSources
            .map((fundingSource) => getPaymentMethodInfo(fundingSource))
            .filter((option) => option !== undefined)}
          optionSelected={({ item }) => {
            setSelectedPaymentMethodId(item.value);
          }}
          selectedItem={fundingSources
            .map((fundingSource) => getPaymentMethodInfo(fundingSource))
            .find((option) => option?.id === selectedPaymentMethodId)}
        />
        <RedoIncrementDecrement
          description={
            error ? `Must add at least $${minAmount.toFixed(2)} to proceed` : ""
          }
          error={amount > 0 && error}
          fullWidth
          label="Amount"
          min={0}
          prefix="$"
          setValue={setAmount}
          size="small"
          step={0.01}
          value={amount}
        />
        {errorMessage && (
          <Text fontSize="sm" textColor="error">
            {errorMessage}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});
